// src/components/common/subscription/QuotaWarning.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, AlertTriangle, AlertCircle, X, Infinity, TrendingUp } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import styles from './QuotaWarning.module.css';

const QuotaWarning = ({ resourceType, usage, severity = 'info', dismissible = true }) => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [isDismissed, setIsDismissed] = useState(false);

  const storageKey = `quota-warning-dismissed-${resourceType}`;

  useEffect(() => {
    if (dismissible) {
      const dismissed = localStorage.getItem(storageKey);
      if (dismissed) {
        const dismissedData = JSON.parse(dismissed);
        const now = Date.now();
        // Auto-show warning again after 24 hours
        if (now - dismissedData.timestamp < 24 * 60 * 60 * 1000) {
          setIsDismissed(true);
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    }
  }, [storageKey, dismissible]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (dismissible) {
      localStorage.setItem(storageKey, JSON.stringify({
        timestamp: Date.now()
      }));
    }
  };

  const handleUpgrade = () => {
    navigate('/subscription/plans');
  };

  // Don't show warning if severity is 'info'
  if (severity === 'info') return null;

  if (isDismissed || !usage) return null;

  // Don't show warning for unlimited resources
  if (usage.isUnlimited) return null;

  const severityConfig = {
    warning: {
      icon: AlertTriangle,
      color: '#f59e0b',
      bgColor: '#fffbeb',
      borderColor: '#fcd34d'
    },
    critical: {
      icon: AlertCircle,
      color: '#ef4444',
      bgColor: '#fef2f2',
      borderColor: '#fca5a5'
    }
  };

  const config = severityConfig[severity];
  if (!config) return null;
  
  const Icon = config.icon;

  const percentage = usage.limit > 0 ? Math.min(100, Math.round((usage.usage / usage.limit) * 100)) : 100;
  const remaining = Math.max(0, usage.limit - usage.usage);

  const getMessage = () => {
    if (usage.period === 'monthly') {
      return t('subscription.quotaWarningMonthly', {
        used: usage.usage,
        limit: usage.limit,
        resource: t(`subscription.${resourceType}`)
      });
    } else if (usage.period === 'daily') {
      return t('subscription.quotaWarningDaily', {
        used: usage.usage,
        limit: usage.limit,
        resource: t(`subscription.${resourceType}`)
      });
    } else {
      return t('subscription.quotaWarningTotal', {
        used: usage.usage,
        limit: usage.limit,
        resource: t(`subscription.${resourceType}`)
      });
    }
  };

  return (
    <div 
      className={`${styles.quotaWarning} ${styles[severity]}`}
      style={{
        backgroundColor: config.bgColor,
        borderColor: config.borderColor
      }}
    >
      <div className={styles.warningContent}>
        <Icon className={styles.icon} size={20} style={{ color: config.color }} />
        <div className={styles.message}>
          <span className={styles.messageText}>
            {getMessage()}
          </span>
          {remaining > 0 && (
            <span className={styles.remainingText}>
              {t('subscription.quotaRemaining', { remaining })}
            </span>
          )}
          {percentage >= 100 && (
            <span className={styles.limitReachedText}>
              {t('subscription.quotaLimitReached')}
            </span>
          )}
        </div>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill}
            style={{ 
              width: `${percentage}%`,
              backgroundColor: config.color
            }}
          />
        </div>
        <span className={styles.percentageText} style={{ color: config.color }}>
          {percentage}%
        </span>
      </div>

      {severity === 'critical' && (
        <div className={styles.actionSection}>
          <button onClick={handleUpgrade} className={styles.upgradeButton}>
            <TrendingUp size={16} />
            {t('subscription.upgradeNow')}
          </button>
        </div>
      )}

      {dismissible && severity !== 'critical' && (
        <button onClick={handleDismiss} className={styles.dismissButton}>
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default QuotaWarning;